// Pipe BOS — état réel (bos_stage), action (bos_action), relances tracées. Aucun envoi automatique.
import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Bank, User, Scales, Check } from "@phosphor-icons/react";

export const WHO = { banque: { label: "Banque", Icon: Bank, cls: "bg-blue-100 text-blue-800" }, client: { label: "Client", Icon: User, cls: "bg-amber-100 text-amber-800" }, notaire: { label: "Notaire", Icon: Scales, cls: "bg-purple-100 text-purple-800" } };

export function WhoPill({ who, testid }) {
  const w = WHO[who];
  if (!w) return <span className="text-slate-400 text-[11px]" data-testid={testid}>—</span>;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded ${w.cls}`} data-testid={testid}><w.Icon size={10} weight="fill" />{w.label}</span>;
}

export function FollowupForm({ dossierId, defaultWho = "banque", onSaved, compact = false }) {
  const [who, setWho] = useState(defaultWho);
  const [canal, setCanal] = useState("telephone");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setWho(defaultWho); }, [defaultWho]);
  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/dossiers/${dossierId}/bos/followups`, { interlocuteur: who, canal, commentaire: comment });
      toast.success(`Relance ${WHO[who].label} enregistrée — prochaine échéance ${formatDate(res.data.due_at)}`);
      setComment(""); onSaved?.(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "" : "mt-2"}`} onClick={(e) => e.stopPropagation()} data-testid={`bos-followup-form-${dossierId}`}>
      <select value={who} onChange={(e) => setWho(e.target.value)} className="px-2 py-1 rounded border border-slate-200 text-[12px] font-semibold bg-white" data-testid={`bos-followup-who-${dossierId}`}>
        {Object.entries(WHO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <select value={canal} onChange={(e) => setCanal(e.target.value)} className="px-2 py-1 rounded border border-slate-200 text-[12px] font-semibold bg-white" data-testid={`bos-followup-canal-${dossierId}`}>
        <option value="telephone">Téléphone</option><option value="email">Email</option><option value="autre">Autre</option>
      </select>
      {!compact && <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (optionnel)" className="flex-1 min-w-[140px] px-2 py-1 rounded border border-slate-200 text-[12px]" data-testid={`bos-followup-comment-${dossierId}`} />}
      <button type="button" onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-600 text-white text-[11.5px] font-extrabold hover:bg-violet-700 disabled:opacity-60" data-testid={`bos-followup-save-${dossierId}`}>
        <Check size={11} weight="bold" /> Relance enregistrée
      </button>
    </div>
  );
}

export default function BosPipeBlock({ dossier, onUpdated }) {
  const [followups, setFollowups] = useState([]);
  const load = () => api.get(`/dossiers/${dossier.id}/bos/followups`).then((r) => setFollowups(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dossier.id]);
  const a = dossier.bos_action;
  return (
    <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3 mb-3 text-[12.5px]" data-testid="bos-pipe-block">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-500 font-semibold">Étape BOS :</span>
        <span className="font-extrabold text-slate-900" data-testid="bos-stage-label">{dossier.bos_stage_label || "—"}</span>
        {a && <>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500 font-semibold">Action :</span>
          <span className="font-bold text-violet-800" data-testid="bos-action-label">{a.label}</span>
          <WhoPill who={a.interlocuteur} testid="bos-action-who" />
        </>}
      </div>
      {(dossier.offre_recue_client_date || dossier.etape_dates?.offre_editee) && (
        <div className="mt-1 text-[11.5px] text-slate-600" data-testid="bos-offre-dates">
          Offre éditée {dossier.etape_dates?.offre_editee ? formatDate(dossier.etape_dates.offre_editee) : "—"} · reçue par le client {dossier.offre_recue_client_date ? formatDate(dossier.offre_recue_client_date) : "—"} · signée {dossier.offre_signee_date ? formatDate(dossier.offre_signee_date) : "—"}
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[11.5px]">
        {[["offre_recue_client_date", "Offre reçue client"], ["offre_signee_date", "Offre signée"], ["deblocage_fonds_previsionnel_date", "Déblocage prévu"]].map(([k, l]) => (
          <label key={k} className="flex flex-col gap-0.5"><span className="text-slate-500 font-semibold">{l}</span>
            <input type="date" value={dossier[k] || ""} onChange={async (e) => { try { const r = await api.patch(`/dossiers/${dossier.id}`, { [k]: e.target.value || null }); onUpdated(r.data); } catch { toast.error("Erreur"); } }} className="px-1.5 py-1 rounded border border-slate-200 bg-white" data-testid={`bos-${k}`} />
          </label>
        ))}
        <label className="flex items-center gap-2 mt-4"><input type="checkbox" checked={!!dossier.deblocage_fonds_prepare} onChange={async (e) => { try { const r = await api.patch(`/dossiers/${dossier.id}`, { deblocage_fonds_prepare: e.target.checked }); onUpdated(r.data); } catch { toast.error("Erreur"); } }} data-testid="bos-deblocage_fonds_prepare" /><span className="text-slate-600 font-semibold">Déblocage préparé</span></label>
      </div>
      {dossier.bos?.why && <div className="mt-1 text-[11.5px] text-slate-600" data-testid="bos-why">{dossier.bos.why}{dossier.bos.due_at ? ` · échéance ${formatDate(dossier.bos.due_at)}` : ""}</div>}
      {a && a.code !== "TERMINE" && (dossier.bos_delegated
        ? <FollowupForm dossierId={dossier.id} defaultWho={a.interlocuteur || "banque"} onSaved={() => { load(); api.get(`/dossiers/${dossier.id}`).then((r) => onUpdated(r.data)).catch(() => {}); }} />
        : <div className="mt-2 text-[11.5px] font-semibold text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1" data-testid="bos-eligible-not-delegated">Éligible / À déléguer — les relances BOS s'enregistrent une fois le dossier délégué.</div>)}
      {followups.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[11.5px]" data-testid="bos-followups-list">
          {followups.slice(0, 5).map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-slate-600"><WhoPill who={f.interlocuteur} /><span className="font-semibold">{formatDate(f.date)}</span><span>{f.canal}</span>{f.commentaire && <span className="truncate">— {f.commentaire}</span>}<span className="ml-auto text-slate-400">→ {formatDate(f.due_at)}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}
